console.log('[APP] 1. Início do App.jsx');
console.log('[APP] 2. Importando BrowserRouter...');
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
console.log('[APP] 3. BrowserRouter importado');
console.log('[APP] 4. Importando ThemeProvider...');
import { ThemeProvider, createTheme } from '@mui/material/styles';
console.log('[APP] 5. ThemeProvider importado');
console.log('[APP] 6. Importando CssBaseline...');
import CssBaseline from '@mui/material/CssBaseline';
console.log('[APP] 7. CssBaseline importado');
console.log('[APP] 7.5. Importando React Query...');
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
console.log('[APP] 7.6. React Query importado');
console.log('[APP] 8. Importando componentes essenciais...');
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import OnboardingRouter from './pages/OnboardingRouter';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ThemeCustomProvider } from './contexts/ThemeCustomContext';
import ThemeApplier from './components/ThemeApplier';
import { authService } from './services/authService';
import { Box, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
console.log('[APP] 8.5. Importando páginas...');

// Import direto das páginas (sem lazy loading)
import DashboardPage from './pages/DashboardPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ServicesPage from './pages/ServicesPage';
import ProfessionalsPage from './pages/ProfessionalsPage';
import PlacesPage from './pages/PlacesPage';
import SchedulesPage from './pages/SchedulesPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import ScheduleBlocksPage from './pages/ScheduleBlocksPage';
import PersonsPage from './pages/PersonsPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PagesPage from './pages/PagesPage';
import PageEditorPage from './pages/PageEditorPage';
import { SupportTicketsPage } from './components/SupportTickets';

console.log('[APP] 8.6. Importando componentes de LGPD...');
import LgpdConsentModal from './components/LgpdConsentModal';

console.log('[APP] 9. Páginas importadas');

console.log('[APP] 10. Criando tema...');
// Tema customizado com otimizações mobile
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    
    // Títulos principais - responsivos
    h4: {
      fontWeight: 600,
      fontSize: '2.125rem',  // 34px desktop
      '@media (max-width:600px)': {
        fontSize: '1.75rem',  // 28px mobile (-18%)
      },
    },
    
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',  // 24px desktop
      '@media (max-width:600px)': {
        fontSize: '1.375rem',  // 22px mobile (-8%)
      },
    },
    
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',  // 20px desktop
      '@media (max-width:600px)': {
        fontSize: '1.125rem',  // 18px mobile (-10%)
      },
    },
    
    // Corpo de texto
    body1: {
      fontSize: '1rem',  // 16px desktop
      '@media (max-width:600px)': {
        fontSize: '0.9375rem',  // 15px mobile (-6%)
      },
    },
    
    body2: {
      fontSize: '0.875rem',  // 14px desktop
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',  // 13px mobile (-7%)
      },
    },
    
    // Botões
    button: {
      textTransform: 'none',
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
      },
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '@media (max-width:600px)': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '@media (max-width:600px)': {
            padding: 12,  // -25% padding mobile
            '&:last-child': {
              paddingBottom: 12,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '@media (max-width:600px)': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (max-width:600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            fontSize: '0.875rem',
          },
        },
      },
    },
  },
});
console.log('[APP] 11. Tema criado:', theme);

// Configuração do React Query
console.log('[APP] 11.5. Criando QueryClient...');
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos - dados considerados "frescos"
      cacheTime: 10 * 60 * 1000, // 10 minutos - tempo em cache
      refetchOnWindowFocus: false, // Não refetch ao focar janela
      retry: 1, // Tentar apenas 1 vez em caso de erro
    },
  },
});
console.log('[APP] 11.6. QueryClient criado');

console.log('[APP] 12. Definindo função App...');
function App() {
  console.log('[APP] 13. App() executando...');
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(true);

  // Detectar isNewUser do JWT
  useEffect(() => {
    try {
      console.log('[APP] Iniciando detecção de isNewUser...');
      console.log('[APP] URL atual:', window.location.href);
      
      // SUPORTE A TESTE: Verificar se há ?newuser=true na URL
      const queryParams = new URLSearchParams(window.location.search);
      const forceNewUser = queryParams.get('newuser') === 'true';
      
      if (forceNewUser) {
        console.log('[APP] 🧪 MODO TESTE: ?newuser=true detectado - limpando apenas estado de onboarding');
        // Limpar APENAS chaves de onboarding, preservar token JWT para as APIs funcionarem
        localStorage.removeItem('agendario_onboarding_session');
        localStorage.removeItem('agendario_theme');
        localStorage.removeItem('agendario_wizard_state');
        sessionStorage.clear();
        setIsNewUser(true);
        setLoading(false);
        return;
      }
      
      // PRIMEIRO: Processar token da URL (se houver)
      const hasNewToken = authService.processLoginCallback();
      console.log('[APP] processLoginCallback retornou:', hasNewToken);
      
      // DEPOIS: Tentar obter userInfo
      console.log('[APP] Token no localStorage:', localStorage.getItem('agendario_jwt_token') ? 'SIM' : 'NÃO');
      
      const userInfo = authService.getUserInfo();
      console.log('[APP] userInfo retornado:', userInfo);
      
      if (userInfo) {
        const newUser = userInfo.isNewUser === true;
        console.log('[APP] isNewUser detectado do JWT:', newUser, '| userInfo.isNewUser:', userInfo.isNewUser, '| type:', typeof userInfo.isNewUser);
        console.log('[APP] setIsNewUser será chamado com:', newUser);
        setIsNewUser(newUser);
      } else {
        console.log('[APP] Nenhuma informação de usuário no JWT - renderizará admin normal');
      }
      setLoading(false);
    } catch (error) {
      console.warn('[APP] Erro ao detectar isNewUser:', error);
      setLoading(false);
    }
  }, []);

  // Log quando isNewUser ou loading mudam
  useEffect(() => {
    console.log('[APP] Estado mudou! isNewUser:', isNewUser, '| loading:', loading);
  }, [isNewUser, loading]);

  console.log('[APP] 14. Retornando JSX... (isNewUser:', isNewUser, ', loading:', loading, ')');

  // Mostrar loading enquanto detecta isNewUser
  if (loading) {
    console.log('[APP] ⏳ RENDERIZANDO: LoadingScreen (detectando isNewUser)');
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  // Se for novo usuário, verificar se já completou onboarding
  const sessionData = localStorage.getItem('agendario_onboarding_session');
  const onboardingCompleted = sessionData ? JSON.parse(sessionData).step === 'completed' : false;
  console.log('[APP] Verificando onboarding:');
  console.log('[APP]   - isNewUser:', isNewUser);
  console.log('[APP]   - sessionData:', sessionData);
  console.log('[APP]   - onboardingCompleted:', onboardingCompleted);
  console.log('[APP]   - Renderizar OnboardingRouter?', isNewUser && !onboardingCompleted);
  
  // Se for novo usuário E NÃO completou onboarding, mostrar wizard
  if (isNewUser && !onboardingCompleted) {
    console.log('[APP] ✅ RENDERIZANDO: OnboardingRouter (novo usuário detectado)');
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeCustomProvider disableThemeSelection={true}>
          <ThemeApplier baseTheme={theme}>
            <CssBaseline />
            <BrowserRouter>
              <OnboardingRouter />
            </BrowserRouter>
          </ThemeApplier>
        </ThemeCustomProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    );
  }

  // Admin normal
  console.log('[APP] ✅ RENDERIZANDO: Admin normal (usuário existing)');
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeCustomProvider>
        <ThemeApplier baseTheme={theme}>
          <CssBaseline />
          <OnboardingProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="appointments" element={<AppointmentsPage />} />
                  <Route path="professionals" element={<ProfessionalsPage />} />
                  <Route path="places" element={<PlacesPage />} />
                  <Route path="services" element={<ServicesPage />} />
                  <Route path="pages" element={<PagesPage />} />
                  <Route path="pages/new/:pageType" element={<PageEditorPage />} />
                  <Route path="pages/edit/:pageId" element={<PageEditorPage />} />
                  <Route path="schedules" element={<SchedulesPage />} />
                  <Route path="persons" element={<PersonsPage />} />
                  <Route path="payment-methods" element={<PaymentMethodsPage />} />
                  <Route path="absences" element={<ScheduleBlocksPage />} />
                  <Route path="support-tickets" element={<SupportTicketsPage />} />
                </Route>
                <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
              </Routes>
              <LgpdConsentModal />
            </BrowserRouter>
          </OnboardingProvider>
        </ThemeApplier>
      </ThemeCustomProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

console.log('[APP] 15. Exportando App...');
export default App;
console.log('[APP] 16. App exportado com sucesso!');
